import { getOperatorsHttpOrigin } from "../../operators";
import {
  isOperatorsAuthExpiredError,
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import { OperatorsForbiddenError } from "../../lib/operatorsReactQuery";

export const DEFAULT_CSV_FILENAME = "jill-dashboard-export.csv";
export const DEFAULT_PNG_FILENAME = "jill-dashboard-chart.png";

export type DashboardsPngExportResponse = {
  chartId: string;
  chartState: Record<string, unknown> | null;
  dataUrl: string | null;
  exportedAt: string;
  ok: true;
};

export function stringifyCsv(rows: Array<Record<string, unknown>>): string {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0] ?? {});
  const escapeCell = (value: unknown) => {
    const str = String(value ?? "");
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replaceAll('"', '""')}"`;
    }
    return str;
  };
  return [
    headers.join(","),
    ...rows.map((row) => headers.map((header) => escapeCell(row[header])).join(",")),
  ].join("\n");
}

export function isDashboardsPngExportResponse(
  value: unknown,
): value is DashboardsPngExportResponse {
  if (!value || typeof value !== "object") return false;
  const body = value as Record<string, unknown>;
  return (
    body.ok === true &&
    typeof body.chartId === "string" &&
    typeof body.exportedAt === "string" &&
    (body.dataUrl === null || typeof body.dataUrl === "string") &&
    (body.chartState === null ||
      (typeof body.chartState === "object" && !Array.isArray(body.chartState)))
  );
}

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }
  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

export function saveBlob(blob: Blob, filename: string): void {
  if (typeof document === "undefined") return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function saveDataUrl(dataUrl: string, filename: string): void {
  if (typeof document === "undefined") return;
  const link = document.createElement("a");
  link.href = dataUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function exportRowsToCsv(
  rows: Array<Record<string, unknown>>,
  filename = DEFAULT_CSV_FILENAME,
): Promise<void> {
  try {
    const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/dashboards/export/csv`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ rows, filename }),
    });
    throwIfUnauthorized(response);
    await throwIfForbidden(response, "You do not have access to Dashboards.");
    if (!response.ok) {
      throw new Error("CSV export endpoint failed");
    }
    saveBlob(await response.blob(), filename);
  } catch (error) {
    if (isOperatorsAuthExpiredError(error) || error instanceof OperatorsForbiddenError) {
      throw error;
    }
    const csv = stringifyCsv(rows);
    saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), filename);
  }
}

export async function exportChartToPng(
  canvasElement: HTMLCanvasElement | null | undefined,
  filename = DEFAULT_PNG_FILENAME,
): Promise<void> {
  if (!canvasElement) return;
  const dataUrl = canvasElement.toDataURL("image/png");
  const chartId = filename.replace(/\.png$/i, "");

  try {
    const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/dashboards/export/png`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ chartId, dataUrl }),
    });
    throwIfUnauthorized(response);
    if (response.ok) {
      const body: unknown = await response.json();
      if (!isDashboardsPngExportResponse(body)) {
        throw new Error("PNG export response was malformed");
      }
    }
  } catch (error) {
    if (isOperatorsAuthExpiredError(error)) {
      saveDataUrl(dataUrl, filename);
      throw error;
    }
    // Local download still proceeds when the echo endpoint is unavailable.
  }

  saveDataUrl(dataUrl, filename);
}
